import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { analyticsAPI } from '../services/api';
import { useTranslation } from 'react-i18next';
import ProfileAvatar from './ProfileAvatar';

interface UserAnalytics {
  totalGamesPlayed: number;
  totalMatchesPlayed: number;
  gamesWon: number;
  matchesWon: number;
  gamesLost: number;
  matchesLost: number;
  winRate: number;
  totalCardsPlayed: number;
  totalCardsDrawn: number;
  totalCoinsSpent: number;
  totalCoinsWon: number;
  totalCoinsLost: number;
  netCoinGain: number;
  coinWinRate: number;
  lowerGreenGamesPlayed: number;
  lowerGreenGamesWon: number;
  raubGamesPlayed: number;
  raubGamesWon: number;
  totalPlayTimeMinutes: number;
  longestWinStreak: number;
  currentWinStreak: number;
  lastGamePlayed: string;
}

interface GameHistory {
  id: string;
  gameId: string;
  gameName: string;
  gameVariant: string;
  gameMode: string;
  result: string;
  finalScore: number;
  position: number;
  entryFee: number;
  coinsWon: number;
  coinsLost: number;
  netCoins: number;
  cardsPlayed: number;
  cardsDrawn: number;
  matchesPlayed: number;
  matchesWon: number;
  durationMinutes: number;
  createdAt: string;
  players: Array<{
    id: string;
    name: string;
    finalScore: number;
    position: number;
  }>;
}

interface LeaderboardEntry {
  user: {
    id: string;
    firstName: string;
    lastName: string;
    profilePicture?: string;
  };
  analytics: UserAnalytics;
}

const History: React.FC = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'overview' | 'history' | 'leaderboard'>('overview');
  const [analytics, setAnalytics] = useState<UserAnalytics | null>(null);
  const [gameHistory, setGameHistory] = useState<GameHistory[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [coinLeaderboard, setCoinLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    gameVariant: '',
    gameMode: '',
    result: '',
  });

  useEffect(() => {
    loadAnalytics();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      loadGameHistory();
    } else if (activeTab === 'leaderboard') {
      loadLeaderboards();
    }
  }, [activeTab, historyPage, filters]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      const response = await analyticsAPI.getUserStats();
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadGameHistory = async () => {
    try {
      const response = await analyticsAPI.getGameHistory({
        page: historyPage,
        limit: 20,
        ...filters,
      });
      setGameHistory(response.data.history);
      setHistoryTotalPages(response.data.totalPages);
    } catch (error) {
      console.error('Error loading game history:', error);
    }
  };

  const loadLeaderboards = async () => {
    try {
      const [leaderboardResponse, coinLeaderboardResponse] = await Promise.all([
        analyticsAPI.getLeaderboard(10),
        analyticsAPI.getCoinLeaderboard(10),
      ]);
      setLeaderboard(leaderboardResponse.data);
      setCoinLeaderboard(coinLeaderboardResponse.data);
    } catch (error) {
      console.error('Error loading leaderboards:', error);
    }
  };

  const formatDuration = (minutes: number): string => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  const getGameVariantLabel = (variant: string): string => {
    switch (variant) {
      case 'lower_green':
        return t('game.lowerGreen');
      case 'raub':
        return t('game.raub');
      default:
        return variant;
    }
  };

  const getResultColor = (result: string): string => {
    switch (result) {
      case 'won':
        return 'success';
      case 'lost':
        return 'error';
      case 'spectated':
        return 'info';
      default:
        return 'default';
    }
  };

  if (loading && !analytics) {
    return (
      <div className="dashboard-modern">
        <div className="dashboard-header">
          <div>
            <h1>📊 Game History & Analytics</h1>
            <p>Track your gaming performance and achievements</p>
          </div>
        </div>
        <div className="game-section">
          <div className="section-header">
            <h3 className="section-title">
              <span className="section-icon">⏳</span>
              Loading Analytics...
            </h3>
          </div>
          <div style={{ 
            textAlign: 'center', 
            padding: '60px',
            color: 'rgba(255,255,255,0.6)'
          }}>
            <div className="spinner-border text-light" role="status" style={{ width: '3rem', height: '3rem' }}>
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-modern">
      {/* History Header */}
      <div className="dashboard-header">
        <div>
          <h1>📊 Game History & Analytics</h1>
          <p>Track your gaming performance, statistics, and achievements</p>
        </div>
      </div>

      {/* Analytics Tabs */}
      <div className="game-actions" style={{ marginBottom: '30px' }}>
        <button
          className={`action-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
          style={{ 
            background: activeTab === 'overview' ? '#7fa650' : 'rgba(255, 255, 255, 0.1)',
            color: activeTab === 'overview' ? '#fff' : 'rgba(255, 255, 255, 0.8)'
          }}
        >
          <span>📈</span>
          <span>{t('analytics.overview')}</span>
        </button>
        <button
          className={`action-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
          style={{ 
            background: activeTab === 'history' ? '#7fa650' : 'rgba(255, 255, 255, 0.1)',
            color: activeTab === 'history' ? '#fff' : 'rgba(255, 255, 255, 0.8)'
          }}
        >
          <span>📋</span>
          <span>{t('analytics.history')}</span>
        </button>
        <button
          className={`action-btn ${activeTab === 'leaderboard' ? 'active' : ''}`}
          onClick={() => setActiveTab('leaderboard')}
          style={{ 
            background: activeTab === 'leaderboard' ? '#7fa650' : 'rgba(255, 255, 255, 0.1)',
            color: activeTab === 'leaderboard' ? '#fff' : 'rgba(255, 255, 255, 0.8)'
          }}
        >
          <span>🏆</span>
          <span>{t('analytics.leaderboard')}</span>
        </button>
      </div>

      {activeTab === 'overview' && analytics && (
        <>
          {/* Overview Stats */}
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#4CAF50' }}>{analytics.totalGamesPlayed}</div>
              <div className="stat-label">{t('analytics.totalGames')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#FF9800' }}>{analytics.gamesWon}</div>
              <div className="stat-label">{t('analytics.gamesWon')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#2196F3' }}>{analytics.winRate}%</div>
              <div className="stat-label">{t('analytics.winRate')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#9C27B0' }}>{analytics.totalMatchesPlayed}</div>
              <div className="stat-label">{t('analytics.totalMatches')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#4CAF50' }}>{analytics.matchesWon}</div>
              <div className="stat-label">{t('analytics.matchesWon')}</div>
            </div>
            <div className="stat-card">
              <div className="stat-value" style={{ color: '#FF5722' }}>{analytics.currentWinStreak}</div>
              <div className="stat-label">{t('analytics.currentStreak')}</div>
            </div>
          </div>

          {/* Coin Statistics */}
          <div className="game-section">
            <div className="section-header">
              <h3 className="section-title">
                <span className="section-icon">💰</span>
                {t('analytics.coinStatistics')}
              </h3>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value" style={{ color: '#4CAF50' }}>{analytics.totalCoinsWon}</div>
                <div className="stat-label">{t('analytics.totalCoinsWon')}</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: '#FF5722' }}>{analytics.totalCoinsSpent}</div>
                <div className="stat-label">{t('analytics.totalCoinsSpent')}</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: analytics.netCoinGain >= 0 ? '#4CAF50' : '#FF5722' }}>
                  {analytics.netCoinGain >= 0 ? '+' : ''}{analytics.netCoinGain}
                </div>
                <div className="stat-label">{t('analytics.netCoinGain')}</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: '#2196F3' }}>{analytics.coinWinRate}%</div>
                <div className="stat-label">{t('analytics.coinWinRate')}</div>
              </div>
            </div>
          </div>

          {/* Game Variants */}
          <div className="game-section">
            <div className="section-header">
              <h3 className="section-title">
                <span className="section-icon">🎮</span>
                {t('analytics.gameVariants')}
              </h3>
            </div>
            <div className="games-grid">
              <div className="game-card modern">
                <div className="game-header">
                  <h3>{t('game.lowerGreen')}</h3>
                </div>
                <div className="game-name-section">
                  <div className="info-row">
                    <span>{t('analytics.played')}:</span>
                    <span>{analytics.lowerGreenGamesPlayed}</span>
                  </div>
                  <div className="info-row">
                    <span>{t('analytics.won')}:</span>
                    <span>{analytics.lowerGreenGamesWon}</span>
                  </div>
                  <div className="info-row">
                    <span>{t('analytics.winRate')}:</span>
                    <span>
                      {analytics.lowerGreenGamesPlayed > 0
                        ? Math.round((analytics.lowerGreenGamesWon / analytics.lowerGreenGamesPlayed) * 100)
                        : 0}%
                    </span>
                  </div>
                </div>
              </div>
              <div className="game-card modern">
                <div className="game-header">
                  <h3>{t('game.raub')}</h3>
                </div>
                <div className="game-name-section">
                  <div className="info-row">
                    <span>{t('analytics.played')}:</span>
                    <span>{analytics.raubGamesPlayed}</span>
                  </div>
                  <div className="info-row">
                    <span>{t('analytics.won')}:</span>
                    <span>{analytics.raubGamesWon}</span>
                  </div>
                  <div className="info-row">
                    <span>{t('analytics.winRate')}:</span>
                    <span>
                      {analytics.raubGamesPlayed > 0
                        ? Math.round((analytics.raubGamesWon / analytics.raubGamesPlayed) * 100)
                        : 0}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Activity */}
          <div className="game-section">
            <div className="section-header">
              <h3 className="section-title">
                <span className="section-icon">⏱️</span>
                {t('analytics.activity')}
              </h3>
            </div>
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-value" style={{ color: '#607D8B' }}>{formatDuration(analytics.totalPlayTimeMinutes)}</div>
                <div className="stat-label">{t('analytics.totalPlayTime')}</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: '#795548' }}>{analytics.totalCardsPlayed}</div>
                <div className="stat-label">{t('analytics.cardsPlayed')}</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: '#FF9800' }}>{analytics.totalCardsDrawn}</div>
                <div className="stat-label">{t('analytics.cardsDrawn')}</div>
              </div>
              <div className="stat-card">
                <div className="stat-value" style={{ color: '#E91E63' }}>{analytics.longestWinStreak}</div>
                <div className="stat-label">{t('analytics.longestStreak')}</div>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === 'history' && (
        <div className="game-section">
          <div className="section-header">
            <h3 className="section-title">
              <span className="section-icon">📋</span>
              {t('analytics.history')}
            </h3>
          </div>
          
          {/* History Filters */}
          <div className="game-actions" style={{ marginBottom: '20px' }}>
            <select
              value={filters.gameVariant}
              onChange={(e) => setFilters({ ...filters, gameVariant: e.target.value })}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                padding: '10px 15px',
                color: '#fff',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="">{t('analytics.allVariants')}</option>
              <option value="lower_green">{t('game.lowerGreen')}</option>
              <option value="raub">{t('game.raub')}</option>
            </select>
            <select
              value={filters.gameMode}
              onChange={(e) => setFilters({ ...filters, gameMode: e.target.value })}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                padding: '10px 15px',
                color: '#fff',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="">{t('analytics.allModes')}</option>
              <option value="friendly">{t('game.friendly')}</option>
              <option value="betting">{t('game.betting')}</option>
            </select>
            <select
              value={filters.result}
              onChange={(e) => setFilters({ ...filters, result: e.target.value })}
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                padding: '10px 15px',
                color: '#fff',
                fontSize: '14px',
                cursor: 'pointer'
              }}
            >
              <option value="">{t('analytics.allResults')}</option>
              <option value="won">{t('analytics.won')}</option>
              <option value="lost">{t('analytics.lost')}</option>
              <option value="spectated">{t('analytics.spectated')}</option>
            </select>
          </div>

          {/* History List */}
          <div className="games-grid">
            {gameHistory.length === 0 ? (
              <div className="game-card modern">
                <div className="empty-state">
                  <h4>No games found</h4>
                  <p>Start playing to see your game history here!</p>
                </div>
              </div>
            ) : (
              gameHistory.map((game) => (
                <div key={game.id} className="game-card modern">
                  <div className="game-header">
                    <h3>{game.gameName}</h3>
                    <span className={`game-status ${getResultColor(game.result)}`}>
                      {t(`analytics.${game.result}`)}
                    </span>
                  </div>
                  <div className="game-name-section">
                    <div className="info-row">
                      <span>{t('analytics.variant')}:</span>
                      <span>{getGameVariantLabel(game.gameVariant)}</span>
                    </div>
                    <div className="info-row">
                      <span>{t('analytics.mode')}:</span>
                      <span>{t(`game.${game.gameMode}`)}</span>
                    </div>
                    <div className="info-row">
                      <span>{t('analytics.score')}:</span>
                      <span>{game.finalScore}</span>
                    </div>
                    <div className="info-row">
                      <span>{t('analytics.position')}:</span>
                      <span>{game.position}</span>
                    </div>
                    {game.gameMode === 'betting' && (
                      <>
                        <div className="info-row">
                          <span>{t('analytics.entryFee')}:</span>
                          <span>{game.entryFee}</span>
                        </div>
                        <div className="info-row">
                          <span>{t('analytics.netCoins')}:</span>
                          <span style={{ color: game.netCoins >= 0 ? '#4CAF50' : '#FF5722' }}>
                            {game.netCoins >= 0 ? '+' : ''}{game.netCoins}
                          </span>
                        </div>
                      </>
                    )}
                    <div className="info-row">
                      <span>{t('analytics.duration')}:</span>
                      <span>{formatDuration(game.durationMinutes)}</span>
                    </div>
                    <div className="info-row">
                      <span>{t('analytics.date')}:</span>
                      <span>{formatDate(game.createdAt)}</span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {historyTotalPages > 1 && (
            <div className="game-actions" style={{ justifyContent: 'center', marginTop: '20px' }}>
              <button
                className="action-btn secondary"
                disabled={historyPage === 1}
                onClick={() => setHistoryPage(historyPage - 1)}
              >
                <span>←</span>
                <span>{t('common.previous')}</span>
              </button>
              <span style={{ 
                color: 'rgba(255, 255, 255, 0.8)', 
                padding: '0 20px',
                fontSize: '14px'
              }}>
                {t('analytics.page')} {historyPage} {t('analytics.of')} {historyTotalPages}
              </span>
              <button
                className="action-btn secondary"
                disabled={historyPage === historyTotalPages}
                onClick={() => setHistoryPage(historyPage + 1)}
              >
                <span>{t('common.next')}</span>
                <span>→</span>
              </button>
            </div>
          )}
        </div>
      )}

      {activeTab === 'leaderboard' && (
        <div className="game-section">
          <div className="section-header">
            <h3 className="section-title">
              <span className="section-icon">🏆</span>
              {t('analytics.leaderboard')}
            </h3>
          </div>
          
          <div className="games-grid">
            {/* Top Players */}
            <div className="game-card modern">
              <div className="game-header">
                <h3>{t('analytics.topPlayers')}</h3>
              </div>
              <div className="game-name-section">
                {leaderboard.length === 0 ? (
                  <div className="empty-state">
                    <h4>No players found</h4>
                    <p>Start playing to see the leaderboard!</p>
                  </div>
                ) : (
                  leaderboard.map((entry, index) => (
                    <div key={entry.user.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 0',
                      borderBottom: index < leaderboard.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
                    }}>
                      <div style={{
                        background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
                        color: '#fff',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '12px'
                      }}>
                        {index + 1}
                      </div>
                      <ProfileAvatar user={entry.user} size={32} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', color: '#fff', fontSize: '14px' }}>
                          {entry.user.firstName} {entry.user.lastName}
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
                          {entry.analytics.gamesWon} {t('analytics.wins')} • {entry.analytics.winRate}% {t('analytics.winRate')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#4ecdc4' }}>
                          {entry.analytics.gamesWon}
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
                          {entry.analytics.winRate}%
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Top Earners */}
            <div className="game-card modern">
              <div className="game-header">
                <h3>{t('analytics.topEarners')}</h3>
              </div>
              <div className="game-name-section">
                {coinLeaderboard.length === 0 ? (
                  <div className="empty-state">
                    <h4>No earners found</h4>
                    <p>Start betting to see the coin leaderboard!</p>
                  </div>
                ) : (
                  coinLeaderboard.map((entry, index) => (
                    <div key={entry.user.id} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '10px 0',
                      borderBottom: index < coinLeaderboard.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
                    }}>
                      <div style={{
                        background: 'linear-gradient(45deg, #ff6b6b, #4ecdc4)',
                        color: '#fff',
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: '700',
                        fontSize: '12px'
                      }}>
                        {index + 1}
                      </div>
                      <ProfileAvatar user={entry.user} size={32} />
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '600', color: '#fff', fontSize: '14px' }}>
                          {entry.user.firstName} {entry.user.lastName}
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
                          {entry.analytics.totalCoinsWon} {t('analytics.coinsWon')} • {entry.analytics.coinWinRate}% {t('analytics.coinWinRate')}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: '16px', fontWeight: '700', color: '#4ecdc4' }}>
                          {entry.analytics.netCoinGain}
                        </div>
                        <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
                          {entry.analytics.coinWinRate}%
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default History; 